import { ScenarioRunScenario } from '../../../api/api';

// What a scenario says once it is done, or before it began: anything else the runner writes means it is going.
const NOT_GOING = ['pending', 'passed', 'failed', 'timedout', 'aborted', 'skipped'];

/**
 * The scenario the page follows: the topmost one that is running, in the order the page shows them.
 *
 * A bulk run can have several going at once. The page stays with the topmost until it is done, so the screen does
 * not hop between them on every update. Read from the status the runner writes, not from the outcome: a scenario
 * whose first check just failed counts as failed there, while it is still busy with the rest.
 */
export const followTarget = (scenarios: ScenarioRunScenario[]): string | undefined =>
    scenarios.find((scenario) => !NOT_GOING.includes((scenario.status ?? '').toLowerCase()))?.id;

/** The states of a part of a document's way that is not finished yet: still to come, being worked on, or waiting. */
export const OPEN_WAY_STATES = ['expected', 'busy', 'waiting', 'retrying'];

/**
 * How far the page has to scroll from where it is now to put the held line in the middle of the screen: halfway
 * between the sticky bar and the bottom edge.
 *
 * @param heldTop Where the held line starts, from the top of the viewport.
 * @param heldHeight How tall the held line is.
 * @param topOffset What covers the top of the viewport.
 * @param viewportHeight The height of the viewport.
 */
export const centerShift = (heldTop: number, heldHeight: number, topOffset: number, viewportHeight: number): number =>
    heldTop + heldHeight / 2 - (topOffset + (viewportHeight - topOffset) / 2);

/** Where the page is and how fast it moves, carried from one frame to the next. */
export interface Glide {
    position: number;
    /** Pixels per second. */
    velocity: number;
}

/** About how long the page takes to settle where it should be, in seconds. */
const SMOOTH_TIME = 0.35;

/** The fastest the page moves, in pixels per second, so a long way reads as a glide rather than a jump. */
const MAX_SPEED = 3000;

/**
 * One frame's step from where the page is towards where it should be, as a critically damped spring.
 *
 * Starts gently and stops gently, and keeps its speed when the target moves mid-way - a phase that starts, a card
 * that grows - so a change of course bends the glide instead of jolting it.
 */
export const glideToward = ({ position, velocity }: Glide, desired: number, elapsedMs: number): Glide => {
    const dt = Math.max(elapsedMs, 1) / 1000;
    const omega = 2 / SMOOTH_TIME;
    const x = omega * dt;
    const decay = 1 / (1 + x + 0.48 * x * x + 0.235 * x * x * x);
    const reach = MAX_SPEED * SMOOTH_TIME;
    const change = Math.min(Math.max(position - desired, -reach), reach);
    const goal = position - change;
    const pull = (velocity + omega * change) * dt;
    let nextVelocity = (velocity - omega * pull) * decay;
    let next = goal + (change + pull) * decay;

    // Never past where it should be: there it stops.
    if ((desired > position) === (next > desired)) {
        next = desired;
        nextVelocity = 0;
    }

    // The last half pixel is not worth a frame: the browser rounds the scroll position anyway.
    if (Math.abs(desired - next) < 0.5 && Math.abs(nextVelocity) < 5) {
        return { position: desired, velocity: 0 };
    }

    return { position: next, velocity: nextVelocity };
};
